/**
 * Premium Badge Components
 * Reusable Pro indicators with shimmer animations
 */

import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { Crown } from 'lucide-react'

interface PremiumBadgeProps {
  className?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?: 'default' | 'shimmer' | 'glow'
}

export function PremiumBadge({ className, size = 'sm', variant = 'default' }: PremiumBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-bold uppercase tracking-wider relative overflow-hidden',
        'bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 text-white rounded-full',
        'shadow-[0_0_0_1px_rgba(251,191,36,0.3)]',
        size === 'xs' && 'text-[8px] px-1 py-[1px] gap-0.5',
        size === 'sm' && 'text-[10px] px-1.5 py-0.5 gap-0.5',
        size === 'md' && 'text-xs px-2 py-0.5 gap-1',
        size === 'lg' && 'text-sm px-3 py-1 gap-1.5',
        variant === 'glow' && 'shadow-[0_0_8px_rgba(251,191,36,0.5),0_0_0_1px_rgba(251,191,36,0.3)]',
        className
      )}
    >
      {(size === 'md' || size === 'lg') && (
        <Crown className={cn(
          'shrink-0',
          size === 'md' && 'w-3 h-3',
          size === 'lg' && 'w-3.5 h-3.5',
        )} />
      )}
      PRO
      {(variant === 'shimmer' || variant === 'glow') && (
        <span
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[pro-shimmer_3s_ease-in-out_infinite]"
          style={{ backgroundSize: '200% 100%' }}
        />
      )}
    </span>
  )
}

/**
 * Animated Pro corner badge for profile pages
 * Shows a glowing, animated Pro indicator at the top of the profile
 */
interface ProProfileCornerBadgeProps {
  className?: string
}

export function ProProfileCornerBadge({ className }: ProProfileCornerBadgeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.3 }}
      className={cn('absolute top-0 left-0 z-10', className)}
    >
      <div className="relative">
        {/* Outer glow ring */}
        <motion.div
          animate={{
            boxShadow: [
              '0 0 8px rgba(251,191,36,0.4), 0 0 16px rgba(251,146,60,0.2)',
              '0 0 12px rgba(251,191,36,0.6), 0 0 24px rgba(251,146,60,0.3)',
              '0 0 8px rgba(251,191,36,0.4), 0 0 16px rgba(251,146,60,0.2)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500"
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
          >
            <Crown className="w-3.5 h-3.5 text-white" />
          </motion.div>
          <span className="text-[10px] font-bold tracking-wider text-white uppercase relative overflow-hidden">
            PRO
            <span
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[pro-shimmer_3s_ease-in-out_infinite]"
              style={{ backgroundSize: '200% 100%' }}
            />
          </span>
        </motion.div>
      </div>
    </motion.div>
  )
}

/**
 * Premium-only lock overlay
 * Wraps content that requires premium
 */
interface PremiumGateProps {
  isPremium: boolean
  children: React.ReactNode
  onUpgrade?: () => void
  label?: string
}

export function PremiumGate({ isPremium, children, onUpgrade, label = 'Premium Feature' }: PremiumGateProps) {
  if (isPremium) {
    return <>{children}</>
  }

  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none select-none">
        {children}
      </div>
      <button
        onClick={onUpgrade}
        className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/5 dark:bg-white/5 rounded-xl backdrop-blur-[1px] cursor-pointer"
      >
        <span className="text-lg">🔒</span>
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
          {label}
        </span>
        <PremiumBadge size="sm" />
      </button>
    </div>
  )
}
